import React, { useCallback } from 'react'
import {
  NormalizedBodyStyles,
  NormalizedColours,
  NormalizedDriveTrains,
  NormalizedFuelTypes,
  NormalizedTransmissionTypes,
  NormalizedVehicleOptions,
  NormalizedEngineSizeFilters,
} from '../../../api-client/enums'
import FilterTag from '../../../components/filter-tag'
import { Mobile, Desktop } from '../../../components/media'
import Modal from '../../../components/modal'
import useMemberships from '../../../hooks/use-memberships'
import {
  defaultLeaseFinanceRange,
  defaultMileageRange,
  defaultPriceRange,
  defaultYearRange,
} from '../config'
import { defaultFiltersState as defaultFilters } from '../reducers'

function isFiltersSet(values) {
  if (
    !(values.searchTerms === '' || values.searchTerms == null) ||
    values.price.start !== defaultFilters.price.start ||
    values.price.end !== defaultFilters.price.end ||
    values.leaseFinance.start !== defaultFilters.leaseFinance.start ||
    values.leaseFinance.end !== defaultFilters.leaseFinance.end ||
    values.year.start !== defaultFilters.year.start ||
    values.year.end !== defaultFilters.year.end ||
    values.mileage.start !== defaultFilters.mileage.start ||
    values.mileage.end !== defaultFilters.mileage.end ||
    Object.keys(values.bodyStyle).length !== 0 ||
    Object.keys(values.makes).length !== 0 ||
    Object.keys(values.models).length !== 0 ||
    Object.keys(values.feature).length !== 0 ||
    Object.keys(values.fuel).length !== 0 ||
    Object.keys(values.engineSize).length !== 0 ||
    Object.keys(values.transmission).length !== 0 ||
    Object.keys(values.drivetrain).length !== 0 ||
    Object.keys(values.colour).length !== 0 ||
    Object.keys(values.listingType).length !== 0 ||
    Object.keys(values.membership).length !== 0
  )
    return true

  return false
}

export default function FilterTags({
  visible,
  onToggle,
  values,
  onTagRemove,
  onClearFilters,
  searchTagsState,
  setSearchTagsState,
}) {
  const [memberships] = useMemberships([])
  const generateTagsArray = useCallback(() => {
    let tagsArr = []

    if (Object.keys(values.colour).length !== 0)
      tagsArr.push(
        ...Object.keys(values.colour).map((id) => (
          <FilterTag key={id} name='colour' value={id} onRemove={onTagRemove}>
            {NormalizedColours[id]}
          </FilterTag>
        )),
      )

    if (Object.keys(values.drivetrain).length !== 0)
      tagsArr.push(
        ...Object.keys(values.drivetrain).map((id) => (
          <FilterTag key={id} name='drivetrain' value={id} onRemove={onTagRemove}>
            {NormalizedDriveTrains[id]}
          </FilterTag>
        )),
      )

    if (Object.keys(values.transmission).length !== 0)
      tagsArr.push(
        ...Object.keys(values.transmission).map((id) => (
          <FilterTag key={id} name='transmission' value={id} onRemove={onTagRemove}>
            {NormalizedTransmissionTypes[id]}
          </FilterTag>
        )),
      )

    if (Object.keys(values.engineSize).length !== 0)
      tagsArr.push(
        ...Object.keys(values.engineSize).map((id) => (
          <FilterTag key={id} name='engineSize' value={id} onRemove={onTagRemove}>
            {NormalizedEngineSizeFilters[id]} Cylinders
          </FilterTag>
        )),
      )

    if (Object.keys(values.fuel).length !== 0)
      tagsArr.push(
        ...Object.keys(values.fuel).map((id) => (
          <FilterTag key={id} name='fuel' value={id} onRemove={onTagRemove}>
            {NormalizedFuelTypes[id]}
          </FilterTag>
        )),
      )

    if (Object.keys(values.feature).length !== 0)
      tagsArr.push(
        ...Object.keys(values.feature).map((id) => (
          <FilterTag key={id} name='feature' value={id} onRemove={onTagRemove}>
            {NormalizedVehicleOptions[id]}
          </FilterTag>
        )),
      )

    if (
      (values.mileage.start !== defaultMileageRange.start ||
        values.mileage.end !== defaultMileageRange.end) &&
      !values.errors.mileageStart &&
      !values.errors.mileageEnd
    )
      tagsArr.push(
        <FilterTag name='mileage' value={values.mileage} onRemove={onTagRemove}>
          {'Mileage: '}
          {Number(values.mileage.start).toLocaleString()}
          {' - '}
          {Number(values.mileage.end).toLocaleString()}
        </FilterTag>,
      )

    if (
      (values.year.start !== defaultYearRange.start || values.year.end !== defaultYearRange.end) &&
      !values.errors.yearStart &&
      !values.errors.yearEnd
    )
      tagsArr.push(
        <FilterTag name='year' value={values.year} onRemove={onTagRemove}>
          Year: {values.year.start} - {values.year.end}
        </FilterTag>,
      )

    if (Object.keys(values.bodyStyle).length !== 0)
      tagsArr.push(
        ...Object.keys(values.bodyStyle).map((id) => (
          <FilterTag key={id} name='bodyStyle' value={id} onRemove={onTagRemove}>
            {NormalizedBodyStyles[id]}
          </FilterTag>
        )),
      )

    if (Object.keys(values.models).length !== 0)
      tagsArr.push(
        ...Object.keys(values.models).map((key) => (
          <FilterTag key={key} name='models' value={key} onRemove={onTagRemove}>
            {values.models[key].name}
          </FilterTag>
        )),
      )
    if (Object.keys(values.makes).length !== 0)
      tagsArr.push(
        ...Object.keys(values.makes).map((id) => (
          <FilterTag key={id} name='makes' value={id} onRemove={onTagRemove}>
            {values.makes[id].name}
          </FilterTag>
        )),
      )
    if (Object.keys(values.membership).length !== 0) {
      tagsArr.push(
        ...Object.keys(values.membership).map((id) => {
          let label = memberships?.memberships?.find((item) => item.programId === id)?.programName
          return (
            <FilterTag key={id} name='membership' value={id} onRemove={onTagRemove}>
              {label}
            </FilterTag>
          )
        }),
      )
    }

    if (values.searchTerms)
      tagsArr.push(
        <FilterTag name='searchTerms' value={values.searchTerms} onRemove={onTagRemove}>
          {values.searchTerms}
        </FilterTag>,
      )

    if (
      (values.price.start !== defaultPriceRange.start ||
        values.price.end !== defaultPriceRange.end) &&
      !values.errors.priceStart &&
      !values.errors.priceEnd
    )
      tagsArr.push(
        <FilterTag name='price' value={values.price} onRemove={onTagRemove}>
          {'Price: '}
          {Number(values.price.start).toLocaleString()}
          {' - '}
          {Number(values.price.end).toLocaleString()}
        </FilterTag>,
      )

    if (
      (values.leaseFinance.start !== defaultLeaseFinanceRange.start ||
        values.leaseFinance.end !== defaultLeaseFinanceRange.end) &&
      !values.errors.leaseFinanceStart &&
      !values.errors.leaseFinanceEnd
    )
      tagsArr.push(
        <FilterTag name='leaseFinance' value={values.leaseFinance} onRemove={onTagRemove}>
          {'Monthly Payment: '}
          {Number(values.leaseFinance.start).toLocaleString()}
          {' - '}
          {Number(values.leaseFinance.end).toLocaleString()}
        </FilterTag>,
      )

    return tagsArr
  }, [
    values.colour,
    values.drivetrain,
    values.transmission,
    values.engineSize,
    values.fuel,
    values.feature,
    values.mileage,
    values.errors.mileageStart,
    values.errors.mileageEnd,
    values.errors.yearStart,
    values.errors.yearEnd,
    values.errors.priceStart,
    values.errors.priceEnd,
    values.errors.leaseFinanceStart,
    values.errors.leaseFinanceEnd,
    values.year,
    values.bodyStyle,
    values.models,
    values.makes,
    values.searchTerms,
    values.price,
    values.leaseFinance,
    values.membership,
    onTagRemove,
    memberships,
  ])

  const tagsArray = generateTagsArray()
  const isClearAllBtnVisibleOnMobile = isFiltersSet(values)
  const isClearAllBtnVisibleOnDesktop = searchTagsState.hasPreviouslyOpened
  const isMoreBtnVisible =
    searchTagsState.maxCountOnFirstRow &&
    generateTagsArray().length - searchTagsState.maxCountOnFirstRow > 0 &&
    !searchTagsState.hasPreviouslyOpened

  return (
    <>
      <Desktop>
        {tagsArray.map((tag, index) => {
          if (
            !searchTagsState.maxCountOnFirstRow ||
            index < searchTagsState.maxCountOnFirstRow ||
            searchTagsState.hasPreviouslyOpened
          )
            return <React.Fragment key={tag.props.name + tag.props.value}>{tag}</React.Fragment>

          return null
        })}

        {isMoreBtnVisible ? (
          <button
            className='more activeBtn'
            onClick={() => setSearchTagsState((prev) => ({ ...prev, hasPreviouslyOpened: true }))}
          >
            +{tagsArray.length - searchTagsState.maxCountOnFirstRow} More
          </button>
        ) : null}

        {isClearAllBtnVisibleOnDesktop && (
          <button
            className='clearAll activeBtn'
            onClick={() => {
              onClearFilters()
              setSearchTagsState((prev) => ({ ...prev, hasPreviouslyOpened: false }))
            }}
          >
            Clear All
          </button>
        )}
      </Desktop>

      <Mobile>
        <button className='flatBtn filterTagsToggler' onClick={() => onToggle(true)}>
          Show active filters
        </button>

        {visible && (
          <Modal onClose={() => onToggle(false)}>
            <div className='filterTagsModal'>
              <p className='modalLabel'>Active filters</p>
              {tagsArray.map((tag) => (
                <React.Fragment key={tag.props.name + tag.props.value}>{tag}</React.Fragment>
              ))}
              {isClearAllBtnVisibleOnMobile && (
                <button className='activeBtn' onClick={onClearFilters}>
                  Clear All
                </button>
              )}
            </div>
          </Modal>
        )}
      </Mobile>

      <style jsx>{`
        .filterTagsToggler {
          margin: 0 0 0 auto;
          color: var(--text-link-color);
          font-size: 1.2rem;
        }

        .filterTagsModal {
          padding: 2rem;

          .modalLabel {
            margin: 0 0 2.1rem;
            color: var(--secondary-text-color);
            font-weight: 700;
            font-size: 1.2rem;
          }

          :global(.filterTag) {
            margin: 0 0.5rem 0.7rem 0;
            font-weight: 700;
            font-size: 1.2rem;
          }
        }

        .activeBtn {
          height: 4.2rem;
          margin: 0.5rem 0.6rem;
          padding: 0 2rem;
          border-radius: 2.1rem;
          color: #fff;
          background: var(--primary-button-bg);
          font-size: 1.6rem;
        }

        @media (max-width: 900px) {
          .activeBtn {
            margin: 0 0.5rem 0.7rem 0;
            font-size: 1.2rem;
            font-weight: 700;
          }

          .filterTagsModal {
            padding: 3rem;
          }
        }
      `}</style>
    </>
  )
}
